import {
  type Column,
  type ColumnDef,
  type CustomerQuote,
  EMPTY_ARRAY,
  formattedUTCDay,
  getAgGridColId,
  isPricingModeRelative,
  negotiationStatusMakerLabel,
  type QuoteStatusColumnParams,
  type TextColumnParams,
  useDefaultColumns,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';

interface UseCustomerQuoteColumns {
  defaultColumns?: (keyof CustomerQuote | Partial<Column>)[];
  source?: 'monitoring' | 'dealer-monitoring' | 'manual-rfqs';
  enableMakerMarketplaceETFRFQFlow?: boolean;
}

export function useCustomerQuoteColumns({
  defaultColumns = EMPTY_ARRAY,
  source = 'dealer-monitoring',
}: UseCustomerQuoteColumns): Column[] {
  const { enableMakerMarketplaceETFRFQFlow } = useFeatureFlag();

  const columns = useMemo(
    () =>
      compact([
        {
          field: 'SubmitTime',
          type: 'date',
          sortable: true,
          initialSort: '-',
        },
        {
          field: 'QuoteStatus',
          type: 'quoteStatus',
          params: {
            tooltipField: 'statusText', // InternalText ?? Text
            rfqProtocolField: 'RFQProtocol',
          } satisfies QuoteStatusColumnParams<CustomerQuote>,
        },
        { field: 'Counterparty', type: 'customer', title: 'Customer' },
        { field: 'MarketAccount', type: 'text', title: 'Account', id: 'marketAccount' },
        {
          field: 'MarketAccount',
          type: 'marketAccountSourceAccountID',
          id: 'customerAccountID',
          initialHide: true,
        },
        { field: 'RFQID', type: 'id' },
        { field: 'tradedSideOrSide', type: 'quoteSide', title: 'Side', id: 'Side' },
        { field: 'symbolOrSymbols', id: 'Symbol', title: 'Symbol', type: 'security' },
        { field: 'OrderQty', type: 'size', params: { currencyField: 'Currency' } },
        { field: 'TradedPx', type: 'price', params: { assetField: 'Symbol' } },
        { field: 'TradedAmt', type: 'size', params: { currencyField: 'AmountCurrency' } },
        { field: 'QuoteID', type: 'id' },

        { field: 'User', type: 'user', initialHide: true },
        source === 'monitoring' && { field: 'SubAccount', type: 'subAccount', initialHide: true },
        { field: 'Timestamp', type: 'date', initialHide: true },
        {
          field: 'NegotiationStatus',
          title: 'Negotiation',
          type: 'text',
          initialWidth: 150,
          params: {
            getLabel: negotiationStatusMakerLabel,
          } satisfies TextColumnParams,
          description: 'Shows if the client has requested any changes to the quote. Empty signifies no open requests.',
          initialHide: true,
        },
        {
          type: 'date',
          field: 'SettleDate',
          title: 'Settlement Date',
          params: { customFormatter: formattedUTCDay },
          initialHide: source !== 'manual-rfqs',
        },
        source === 'monitoring' && { field: 'MessageID', type: 'text', initialHide: true },
        { field: 'QuoteReqID', type: 'id', initialHide: true },
        source === 'monitoring' && { field: 'QuoteRequestRejectReason', type: 'text', initialHide: true },
        { field: 'EndTime', type: 'date', initialHide: true },
        { field: 'TradedQty', type: 'size', params: { currencyField: 'Currency' }, initialHide: true },
        { field: 'TradedSide', type: 'side', initialHide: true },
        source === 'monitoring' && { field: 'Group', type: 'text', initialHide: true },
        { field: 'Text', type: 'text', initialHide: true },
        { field: 'Parameters', type: 'text', initialHide: true },
        source === 'monitoring' && { field: 'Comments', type: 'text', initialHide: true },
        {
          field: 'BidPx',
          type: 'price',
          params: {
            assetField: 'Symbol',
            isReferencePrice: (data: CustomerQuote) => isPricingModeRelative(data.PricingMode),
          },
          initialHide: source !== 'manual-rfqs',
        },
        { field: 'BidAmt', type: 'size', params: { currencyField: 'AmountCurrency' }, initialHide: true },
        {
          field: 'OfferPx',
          type: 'price',
          params: {
            assetField: 'Symbol',
            isReferencePrice: (data: CustomerQuote) => isPricingModeRelative(data.PricingMode),
          },
          initialHide: source !== 'manual-rfqs',
        },
        { field: 'OfferAmt', type: 'size', params: { currencyField: 'AmountCurrency' }, initialHide: true },
        { field: 'ValidUntilTime', type: 'date', initialHide: true },
        source === 'monitoring' && { field: 'ReferenceQuoteID', type: 'text', initialHide: true },
        source === 'monitoring' && { field: 'InternalQuoteRequestRejectReason', type: 'text', initialHide: true },
        { field: 'CustomerUser', type: 'text', initialHide: true },
        { field: 'ExpectedHedgeBidPx', type: 'price', params: { assetField: 'Symbol' }, initialHide: true },
        { field: 'ExpectedHedgeOfferPx', type: 'price', params: { assetField: 'Symbol' }, initialHide: true },
        { field: 'EffectiveBidSpread', type: 'text', initialHide: true },
        enableMakerMarketplaceETFRFQFlow && {
          field: 'PricingMode',
          type: 'text',
          title: 'Pricing Mode',
          initialHide: true,
        },
        { field: 'EffectiveOfferSpread', type: 'text', initialHide: true },
        { title: 'Allowed Slippage', type: 'text', id: 'allowedSlippage', initialHide: true },
        { field: 'OrigQuoteReqID', type: 'id', initialHide: true },
        { field: 'InternalText', type: 'text', initialHide: true },
        {
          field: 'PricingReference',
          type: 'price',
          params: {
            isReferencePrice: true,
          },
          initialHide: true,
        },
        {
          field: 'fixingIndices',
          title: 'Fixing Index',
          type: 'fixingIndices',
          id: 'fixingIndices',
          initialHide: source !== 'manual-rfqs',
        },
        {
          field: 'FixingDetails',
          title: 'Fixing Price',
          type: 'fixingPrice',
          id: 'fixingPrice',
          initialHide: source !== 'manual-rfqs',
        },
        { field: 'ExternalGroup', type: 'text', title: 'External ID', initialHide: true },
        enableMakerMarketplaceETFRFQFlow && {
          field: 'postTradeClearingSymbol',
          type: 'text',
          title: 'Post Trade Clearing Symbol',
          initialHide: true,
        },
        enableMakerMarketplaceETFRFQFlow && {
          field: 'postTradeClearingID',
          type: 'id',
          title: 'Post Trade Clearing ID',
          initialHide: true,
        },
      ] satisfies (ColumnDef<CustomerQuote> | false)[]),
    [enableMakerMarketplaceETFRFQFlow, source]
  );

  const columnDefinitions = useMemo(() => new Map(columns.map(c => [getAgGridColId(c), c])), [columns]);
  return useDefaultColumns(defaultColumns, columnDefinitions);
}
